import { useIsOfflineRecordingEnabled } from '@/app-shell/flags/desktop-flags';
import { useIsNewSessionListEnabled } from '@/app-shell/flags/session-flags';
import { offlineActiveRecordingIdAtom } from '@/atoms/offline-recording';
import {
  isBrowserDefinitelyOfflineNow,
  useOfflineCaptureModeState,
} from '@/hooks/desktop/useIsOfflineCaptureMode';
import { useIsOfflineConnectivity } from '@/hooks/desktop/useIsOfflineConnectivity';
import {
  getActiveRecordingSignal,
  useRecordingActiveRefusal,
} from '@/hooks/mutation/recordingActiveGuard';
import {
  beginSessionCreateAttempt,
  cancelSessionCreateAttempt,
  classifySessionCreateError,
  failSessionCreateAttempt,
  markSessionCreateMode,
  type SessionCreateAttemptOwner,
  type SessionCreateMetadata,
} from '@/lib/telemetry/sessionCreateTracking';
import { connectivityBridge } from '@/tauri/offline-recording';
import { type OfflineConnectivityState } from '@/tauri/offline-recording/offline-recording';
import { isTauri } from '@/tauri/utils';
import { isNetworkCreateError } from '@/utils/http/networkCreateError';
import { logger } from '@/utils/logger/logger';
import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@heidi/design-system/toast';
import { sanitizeError } from '@heidi/runtime/logger';
import { type SessionCreatedEntryPoint } from '@heidi/session/contracts';
import { activeSessionIdAtom } from '@heidi/session/data';
import {
  selectedReviewMemberAtom,
  useCombinedQueriesTeamGeneralInfo,
} from '@heidi/workspace/data';
import { type Mutation, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useAtomValue, useStore } from 'jotai';
import { type ParsedUrlQueryInput } from 'querystring';
import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import {
  CREATE_NEW_SESSION_MUTATION_KEY,
  useMutateCreateNewSession,
} from './mutation/useMutateCreateNewSession';
import {
  CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY,
  useMutateCreateNewSessionForOtherUser,
} from './mutation/useMutateCreateNewSessionForOtherUser';
import {
  CREATE_OFFLINE_SESSION_MUTATION_KEY,
  useMutateCreateOfflineSession,
} from './mutation/useMutateCreateOfflineSession';
import { useIsReviewingOtherUser } from './query/useQueryUser';

interface UseNewSessionOptions {
  entryPoint?: SessionCreatedEntryPoint;
  queryParams?: ParsedUrlQueryInput;
  /** Park Start Now so the opened session auto-starts transcription. */
  autoStartTranscription?: boolean;
}

/**
 * Passed only by an explicit user action. It opens the `session_create`
 * attempt once the create is accepted; autonomous callers omit it.
 */
export interface SessionCreateTracking {
  metadata?: SessionCreateMetadata;
}

const CONNECTIVITY_SNAPSHOT_TIMEOUT_MS = 1_000;

const messages = defineMessages({
  offlineCaptureActiveTitle: {
    defaultMessage: 'A session is already in progress',
    id: 'jOg9ErTDN1',
    description:
      'Toast title. The clinician tried to start a new session while another session is still capturing audio on this device. Nothing was created.',
  },
  offlineCaptureActiveDescription: {
    defaultMessage: 'Stop it before starting a new one.',
    id: 'Po+00Ro92C',
    description:
      'Toast body under "A session is already in progress". "It" is the session that is still capturing.',
  },
});

const isOfflineConnectivitySnapshot = (state: OfflineConnectivityState | null): boolean =>
  state === 'offline' || state === 'backend_unreachable';

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('connectivity_snapshot_timeout'));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

/**
 * Why `createNewSession` created nothing. A fire-and-forget caller that waits
 * for the create to navigate (the `/new-session` page) needs it, or it waits
 * forever. `captureSessionId` is the session the live offline capture belongs to.
 */
export type NewSessionRefusal =
  | {
      reason: 'offline_capture_active';
      captureSessionId: string;
    }
  | {
      reason: 'recording_active';
      activeSessionId: string | null;
    };

/**
 * Orchestrates mutation hooks and other client state to provide a clean interface to create a new clinical session
 */
export const useNewSession = ({
  entryPoint,
  queryParams,
  autoStartTranscription = false,
}: UseNewSessionOptions = {}) => {
  const {
    mutate: onNewSession,
    isPending,
    isError,
  } = useMutateCreateNewSession({
    navigate: true,
    queryParams,
    entryPoint,
    autoStartTranscription,
    // This hook owns the offline-capture fallback below, so it is the one
    // caller allowed to suppress the mutation's network-error toast.
    handlesOfflineCaptureFallback: true,
  });

  const {
    mutate: onNewSessionForOtherUser,
    isPending: isPendingOtherUser,
    isError: isErrorOtherUser,
  } = useMutateCreateNewSessionForOtherUser({
    navigate: true,
    autoStartTranscription,
  });

  // Offline capture entry point — routed in when the residency-pinned backend
  // is unreachable (US2), gated behind the offline-recording flag.
  // Intentionally does NOT park Start Now: offline create has its own capture
  // arming path and must not auto-start the online transcription consumer.
  const {
    mutate: onNewSessionOffline,
    isPending: isPendingOffline,
    isError: isErrorOffline,
  } = useMutateCreateOfflineSession();
  const { canOfflineCapture, isOfflineCaptureMode } = useOfflineCaptureModeState();
  const isOfflineConnectivity = useIsOfflineConnectivity();
  const isOfflineRecordingEnabled = useIsOfflineRecordingEnabled();
  const canAttemptOfflineSessionCreate = isOfflineRecordingEnabled && isTauri();

  const isReviewingOtherUser = useIsReviewingOtherUser();
  const { teamDetails, isLoadingTeamDetails } = useCombinedQueriesTeamGeneralInfo();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const jotaiStore = useStore();
  const queryClient = useQueryClient();
  const refuseRecordingActive = useRecordingActiveRefusal();
  const canCreateForOtherUser = Boolean(teamDetails?.team.id && selectedReviewUser);
  const isSessionCreatePending = isPending || isPendingOtherUser || isPendingOffline;
  // New list is always self → always ready. Old list is ready only once the reviewed member
  // and team resolve (a create-for-other needs both).
  const isReady =
    isNewSessionListEnabled ||
    !isReviewingOtherUser ||
    (!isLoadingTeamDetails && canCreateForOtherUser);
  const [isRouting, setIsRouting] = useState(false);
  const isRoutingRef = useRef(false);
  // Any creator instance, not just this hook's: a create already in flight
  // owns the shared attempt, so a second one must not open another.
  const isAnyCreatePending = useNewSessionMutationPending();
  // Per-call mutate callbacks never fire once the caller unmounts, so the
  // offline fallback below cannot run then.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // A live or paused offline capture owns the recorder until it stops, so both
  // creators refuse here, for every entry point. Read at call time so a capture
  // started this tick counts.
  const refuseDuringOfflineCapture = useCallback((): NewSessionRefusal | undefined => {
    const captureSessionId = jotaiStore.get(offlineActiveRecordingIdAtom);
    if (captureSessionId === null) {
      return undefined;
    }
    datadogRum.addAction('new_session_refused', {
      entryPoint,
      reason: 'offline_capture_active',
    });
    toast({
      title: createElement(FormattedMessage, messages.offlineCaptureActiveTitle),
      description: createElement(
        FormattedMessage,
        messages.offlineCaptureActiveDescription
      ),
    });
    return { reason: 'offline_capture_active', captureSessionId };
  }, [entryPoint, jotaiStore]);

  const stopRouting = useCallback(() => {
    isRoutingRef.current = false;
    setIsRouting(false);
  }, []);

  const createNewSession = useCallback(
    (tracking?: SessionCreateTracking): NewSessionRefusal | undefined => {
      let attempt: SessionCreateAttemptOwner | undefined;
      const failRouting = (error: unknown) => {
        stopRouting();
        if (attempt) failSessionCreateAttempt(error);
        // A classification, never the raw error: its message can carry PII.
        logger.error('[useNewSession] createNewSession threw', {
          errorName: error instanceof Error ? error.name : typeof error,
          error: sanitizeError(error),
          isReviewingOtherUser,
        });
        datadogRum.addError(classifySessionCreateError(error), {
          source: 'use-new-session.createNewSession',
          isReviewingOtherUser,
        });
      };
      try {
        if (isRoutingRef.current || isSessionCreatePending) {
          return;
        }
        const refusal = refuseDuringOfflineCapture();
        if (refusal) {
          return refusal;
        }
        const signal = getActiveRecordingSignal({ jotaiStore, queryClient });
        if (signal) {
          datadogRum.addAction('new_session_refused', {
            entryPoint,
            reason: 'recording_active',
            signal,
          });
          refuseRecordingActive(entryPoint, signal);
          return {
            reason: 'recording_active',
            activeSessionId: jotaiStore.get(activeSessionIdAtom),
          };
        }
        isRoutingRef.current = true;
        setIsRouting(true);
        if (tracking && !isAnyCreatePending) {
          beginSessionCreateAttempt(tracking.metadata);
          attempt = {};
        }

        datadogRum.addAction('new_session_requested', {
          entryPoint,
          isReviewingOtherUser,
          canCreateForOtherUser,
          isNewSessionListEnabled,
        });

        const routeOffline = () => {
          if (attempt) markSessionCreateMode('offline');
          onNewSessionOffline(
            { attempt },
            {
              onSettled: stopRouting,
            }
          );
        };

        const routeOnline = () => {
          // Old list (flag off) has no create-for picker: a create-while-reviewing routes to
          // the reviewed member via the shared review atom.
          if (!isNewSessionListEnabled && isReviewingOtherUser) {
            if (!canCreateForOtherUser) {
              if (attempt) cancelSessionCreateAttempt('not_ready');
              stopRouting();
              return;
            }
            if (attempt) markSessionCreateMode('online');
            onNewSessionForOtherUser(
              {
                teamId: teamDetails!.team.id,
                memberId: selectedReviewUser!,
                attempt,
              },
              {
                onSettled: stopRouting,
              }
            );
          } else {
            let routedOfflineAfterNetworkError = false;
            // One predicate for both sides: the create leaves the attempt open
            // exactly when this hook will re-create offline.
            const fallsBackOffline = (error: unknown): boolean =>
              isMountedRef.current &&
              canAttemptOfflineSessionCreate &&
              isNetworkCreateError(error);
            if (attempt) {
              markSessionCreateMode('online');
              attempt.defersToOfflineFallback = fallsBackOffline;
            }
            onNewSession(attempt ? { attempt } : undefined, {
              onError: (error: unknown) => {
                if (fallsBackOffline(error)) {
                  routedOfflineAfterNetworkError = true;
                  routeOffline();
                }
              },
              onSettled: () => {
                if (!routedOfflineAfterNetworkError) {
                  stopRouting();
                }
              },
            });
          }
        };

        // Route into offline capture using the same predicate as the visible
        // offline-capture surfaces, so New Session cannot drift from the real
        // connectivity state.
        if (
          canAttemptOfflineSessionCreate &&
          (isOfflineCaptureMode ||
            isOfflineConnectivity ||
            (canOfflineCapture && isBrowserDefinitelyOfflineNow()))
        ) {
          routeOffline();
          return;
        }

        if (!isTauri()) {
          routeOnline();
          return;
        }

        void withTimeout(connectivityBridge.read(), CONNECTIVITY_SNAPSHOT_TIMEOUT_MS)
          .then(
            (rustConnectivity) =>
              canAttemptOfflineSessionCreate &&
              isOfflineConnectivitySnapshot(rustConnectivity),
            (error: unknown) => {
              logger.warn({
                msg: '[useNewSession] failed to read Rust connectivity snapshot',
                errorName: error instanceof Error ? error.name : typeof error,
              });
              return false;
            }
          )
          .then((isOffline) => (isOffline ? routeOffline() : routeOnline()))
          .catch(failRouting);
      } catch (error) {
        failRouting(error);
      }
    },
    [
      canCreateForOtherUser,
      canAttemptOfflineSessionCreate,
      canOfflineCapture,
      entryPoint,
      isAnyCreatePending,
      isNewSessionListEnabled,
      isOfflineConnectivity,
      isOfflineCaptureMode,
      isReviewingOtherUser,
      isSessionCreatePending,
      onNewSession,
      onNewSessionForOtherUser,
      onNewSessionOffline,
      refuseDuringOfflineCapture,
      queryClient,
      jotaiStore,
      refuseRecordingActive,
      selectedReviewUser,
      stopRouting,
      teamDetails,
    ]
  );

  // Intentionally bypasses isReviewingOtherUser — this is for the assistant flow where
  // the user picks a clinician from an inline dropdown without entering "review" mode.
  // Server-side RBAC (TEAM_OTHERS_SESSION_CREATE) is the authoritative permission gate.
  const createNewSessionForMember = useCallback(
    (
      memberId: string,
      tracking?: SessionCreateTracking
    ): NewSessionRefusal | undefined => {
      let attempt: SessionCreateAttemptOwner | undefined;
      try {
        const refusal = refuseDuringOfflineCapture();
        if (refusal) return refusal;
        const signal = getActiveRecordingSignal({ jotaiStore, queryClient });
        if (signal) {
          datadogRum.addAction('new_session_refused', {
            entryPoint,
            reason: 'recording_active',
            signal,
          });
          refuseRecordingActive(entryPoint, signal);
          return {
            reason: 'recording_active',
            activeSessionId: jotaiStore.get(activeSessionIdAtom),
          };
        }
        datadogRum.addAction('new_session_for_member_requested', {
          entryPoint,
          hasTeam: Boolean(teamDetails?.team.id),
        });
        if (!teamDetails?.team.id) return;
        if (tracking && !isAnyCreatePending) {
          beginSessionCreateAttempt(tracking.metadata);
          markSessionCreateMode('online');
          attempt = {};
        }
        onNewSessionForOtherUser({
          teamId: teamDetails.team.id,
          memberId,
          attempt,
        });
      } catch (error) {
        if (attempt) failSessionCreateAttempt(error);
        logger.error('[useNewSession] createNewSessionForMember threw', {
          errorName: error instanceof Error ? error.name : typeof error,
          error: sanitizeError(error),
        });
        datadogRum.addError(classifySessionCreateError(error), {
          source: 'use-new-session.createNewSessionForMember',
        });
      }
    },
    [
      entryPoint,
      isAnyCreatePending,
      teamDetails,
      onNewSessionForOtherUser,
      refuseDuringOfflineCapture,
      queryClient,
      jotaiStore,
      refuseRecordingActive,
    ]
  );

  return {
    createNewSession,
    createNewSessionForMember,
    isError: isError || isErrorOtherUser || isErrorOffline,
    isPending: isSessionCreatePending || isRouting,
    isReady,
  };
};

/**
 * Returns true if any session creation mutation is pending.
 *
 * APP-8158: Now includes offline session creation mutation key so that
 * SessionPageContent correctly shows the loading state when creating an
 * offline session during connectivity transitions.
 */
export const useNewSessionMutationPending = () => {
  const pending = useMutationState({
    filters: { mutationKey: [CREATE_NEW_SESSION_MUTATION_KEY] },
    select: (mut: Mutation) => mut.state.status === 'pending',
  }).some(Boolean);
  const pendingForOther = useMutationState({
    filters: { mutationKey: [CREATE_NEW_SESSION_FOR_OTHER_USER_MUTATION_KEY] },
    select: (mut: Mutation) => mut.state.status === 'pending',
  }).some(Boolean);
  // APP-8158: Include offline session creation in pending check
  const pendingOffline = useMutationState({
    filters: { mutationKey: [CREATE_OFFLINE_SESSION_MUTATION_KEY] },
    select: (mut: Mutation) => mut.state.status === 'pending',
  }).some(Boolean);
  return pending || pendingForOther || pendingOffline;
};
